import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleApi } from "../../apis";
import { POST_BILL_DQR, GET_BILL_DQR_STATUS } from "../../apis/configuration";
import { LanguageContext } from "../../context/LanguageContext";
import { useSnackbar } from "../../context/SnackbarContext";

const POLL_INTERVAL_MS = 3000;
const MAX_INTERVAL_MS = 10000;
const FAILED_STATUS_PATTERN = /FAIL|CANCEL|EXPIRE/i;

const extractErrorMessage = (err, fallback) => err?.response?.data?.message || fallback;

const useBillDqr = () => {
  const navigate = useNavigate();
  const [I18n] = useContext(LanguageContext);
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [dqrDetails, setDqrDetails] = useState(null);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [paymentReceived, setPaymentReceived] = useState(null);
  const [totalPayment, setTotalPayment] = useState(null);
  const pollTimeoutIdRef = useRef(null);
  const requestIdRef = useRef(0);
  const baselineAmountPaidRef = useRef(0);

  const lastPendingMessageRef = useRef("");

  useEffect(() => {
    return () => clearTimeout(pollTimeoutIdRef.current);
  }, []);

  const generateDqr = useCallback(
    async (billingKey, accessToken) => {
      const requestId = ++requestIdRef.current;
      clearTimeout(pollTimeoutIdRef.current);
      setLoading(true);
      setError("");
      setIsPaymentCompleted(false);
      setPaymentReceived(null);
      setTotalPayment(null);

      try {
        const response = await handleApi(POST_BILL_DQR, { billing_key: billingKey, access_token: accessToken }, null, navigate);
        if (requestIdRef.current !== requestId) return { success: false };

        if (!response?.success || !response?.payment_details) {
          const message = response?.message || I18n.something_went_wrong;
          setError(message);
          return { success: false, message };
        }

        setDqrDetails({
          virtualIban: response.payment_details.virtual_iban,
          expiresAt: response.payment_details.expires_at,
          dqr: response.payment_details.dqr,
          accountTitle: response.payment_details.account_title,
        });

        setTotalPayment(response.amount);
        setPaymentReceived(response.amount_paid);
        baselineAmountPaidRef.current = Number(response.amount_paid) || 0;

        return { success: true };
      } catch (err) {
        if (requestIdRef.current !== requestId) return { success: false };
        const message = extractErrorMessage(err, I18n.something_went_wrong);
        setError(message);
        return { success: false, message };
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [navigate, I18n],
  );

  const pollPaymentStatus = useCallback(
    (billingKey, accessToken, requestId) =>
      new Promise((resolve) => {

        const startedAt = Date.now();

        const attempt = async () => {
          if (requestIdRef.current !== requestId) {
            resolve({ success: false });
            return;
          }

          if (Date.now() - startedAt >= MAX_INTERVAL_MS) {
            const message = "Payment not received";

            setError(message);

            showSnackbar({
              message,
              severity: "error",
            });

            resolve({
              success: false,
              message,
              timedOut: true,
            });

            return;
          }

          try {
            const response = await handleApi(GET_BILL_DQR_STATUS, { billing_key: billingKey, access_token: accessToken }, null, navigate);
           
            if (requestIdRef.current !== requestId) {
              resolve({ success: false });
              return;
            }

            if (response?.success === false || FAILED_STATUS_PATTERN.test(response?.bill_payment_status || "")) {
              const message = response?.message || I18n.something_went_wrong;
              setError(message);
              resolve({ success: false, message });
              return;
            }

            const newAmountPaid = Number(response?.amount_paid);
            if (
              response?.success &&
              !Number.isNaN(newAmountPaid) &&
              newAmountPaid > baselineAmountPaidRef.current
            ) {
              baselineAmountPaidRef.current = newAmountPaid;
              setPaymentReceived(newAmountPaid);
              setTotalPayment(response.amount);
              setIsPaymentCompleted(true);
              resolve({
                success: true,
                paymentReceived: newAmountPaid,
                totalPayment: response.amount,
              });
              return;
            }

            if (response?.message && response.message !== lastPendingMessageRef.current) {
              lastPendingMessageRef.current = response.message;
              showSnackbar({ message: response.message, severity: "info" });
            }

            pollTimeoutIdRef.current = setTimeout(attempt, POLL_INTERVAL_MS);
          } catch (err) {
            if (requestIdRef.current !== requestId) {
              resolve({ success: false });
              return;
            }
            const message = extractErrorMessage(err, I18n.something_went_wrong);
            setError(message);
            resolve({ success: false, message });
          }
        };

        attempt();
      }),
    [navigate, I18n, showSnackbar],
  );

  const verifyPayment = useCallback(
    async (billingKey, accessToken) => {
      const requestId = ++requestIdRef.current;
      clearTimeout(pollTimeoutIdRef.current);
      lastPendingMessageRef.current = "";
      setVerifying(true);
      setError("");

      try {
        return await pollPaymentStatus(billingKey, accessToken, requestId);
      } finally {
        if (requestIdRef.current === requestId) setVerifying(false);
      }
    },
    [pollPaymentStatus],
  );

  const resetDqr = useCallback(() => {
    requestIdRef.current += 1;
    clearTimeout(pollTimeoutIdRef.current);
    lastPendingMessageRef.current = "";
    baselineAmountPaidRef.current = 0;
    setDqrDetails(null);
    setIsPaymentCompleted(false);
    setPaymentReceived(null);
    setTotalPayment(null);
    setError("");
    setLoading(false);
    setVerifying(false);
  }, []);

  const continueForRemainingPayment = useCallback(() => {
    setIsPaymentCompleted(false);
    setPaymentReceived(null);
    setTotalPayment(null);
    setError("");
  }, []);

  return {
    loading,
    verifying,
    error,
    dqrDetails,
    isPaymentCompleted,
    paymentReceived,
    totalPayment,
    generateDqr,
    verifyPayment,
    resetDqr,
    continueForRemainingPayment,
  };
};

export default useBillDqr;
